import React, { useState, useRef } from 'react';
import '../assets/css/CareerFormSection.css';

const jobs = {
  'ui-ux-designer': {
  title: 'UI/UX Designer',
  description: (
    <>
      <p><strong>✨ UI/UX Designer – Shape Seamless User Experiences!</strong></p>
      <p>
        We’re looking for a talented and passionate UI/UX Designer to join our team at Peony Technologies. If you’re driven by creating intuitive, user-friendly designs and love transforming complex ideas into smooth digital experiences — this is your opportunity to shine!
      </p>
      <hr />
      <h4>🔧 What You’ll Be Doing:</h4>
      <ul>
        <li>Design modern and engaging user interfaces for web and mobile applications.</li>
        <li>Develop wireframes, prototypes, and high-fidelity mockups that bring ideas to life.</li>
        <li>Conduct user research, gather insights, and translate them into impactful design solutions.</li>
        <li>Collaborate with developers and product teams to ensure seamless integration between design and code.</li>
        <li>Ensure designs align with our brand identity and maintain consistency across platforms.</li>
        <li>Continuously propose innovative ideas to enhance user experience.</li>
      </ul>
      <hr />
      <h4>🧠 What You Bring:</h4>
      <ul>
        <li>1–2 years of professional experience in UI/UX design.</li>
        <li>A strong <strong>portfolio</strong> showcasing your design process, interactive design, and user experience work.</li>
        <li>Proficiency in <strong>Figma</strong>, <strong>Sketch</strong>, or <strong>Adobe XD</strong>.</li>
        <li>Solid understanding of interactive design principles and user-centered design methodologies.</li>
        <li>Strong problem-solving skills and creative thinking abilities.</li>
        <li>Excellent communication and teamwork skills.</li>
      </ul>
      <hr />
      <h4>🌟 Bonus Points If You:</h4>
      <ul>
        <li>Have experience with motion design or micro-interactions.</li>
        <li>Are familiar with HTML/CSS basics to bridge design and development.</li>
        <li>Keep up with the latest UI/UX trends and tools.</li>
        <li>Enjoy working in agile environments and can juggle multiple projects smoothly.</li>
      </ul>
      <p><strong>Location: Nablus, Palestine (Hybrid or Remote)</strong></p>
    </>
  ),
},
  'full-stack-developer': {
    title: 'Full Stack Developer',
    description: (
      <>
        <p><strong>Full Stack Developer – Join Our Agile Tech Team!</strong></p>
        <p>
          We’re looking for a highly motivated Full Stack Developer who’s passionate about building digital products that make a real impact.
        </p>
        <hr />
        <h4>🔧 What You’ll Do:</h4>
        <ul>
          <li>Develop responsive, intuitive UIs using modern front-end frameworks (React preferred).</li>
          <li>Build and maintain scalable, efficient server-side logic and database schemas.</li>
          <li>Design and integrate RESTful APIs connecting front-end and back-end components.</li>
          <li>Collaborate with product managers, designers, and fellow engineers to bring ideas to life.</li>
          <li>Maintain high-quality code by participating in peer reviews and following best practices.</li>
          <li>Debug and upgrade existing applications to enhance performance and reliability.</li>
        </ul>
        <hr />
        <h4>📌 What We’re Looking For:</h4>
        <ul>
          <li>1–3 years of hands-on experience as a Full Stack Developer.</li>
          <li>Solid skills in HTML, CSS, JavaScript, and a modern framework such as <strong>React, Angular, or Vue.js</strong>.</li>
          <li>Back-end experience with <strong>Node.js (Express) or Python (Flask/Django)</strong>.</li>
          <li>
            Comfortable working with both:
            <ul>
              <li>Relational databases: <strong>PostgreSQL, MySQL</strong></li>
              <li>Non-relational databases: <strong>MongoDB</strong></li>
            </ul>
          </li>
          <li>Strong understanding of version control systems, especially <strong>Git</strong>.</li>
          <li>A proactive problem-solver with excellent debugging and analytical skills.</li>
          <li>Ability to work independently and within a collaborative agile team.</li>
        </ul>
        <hr />
        <h4>🎁 Bonus Points If You Have:</h4>
        <ul>
          <li>Experience with TypeScript or Next.js.</li>
          <li>Familiarity with Docker or CI/CD pipelines.</li>
          <li>Exposure to cloud platforms (AWS, Azure, or GCP).</li>
          <li>Understanding of unit testing frameworks like Jest or Mocha.</li>
          <li>An eye for UX/UI design and passion for clean, usable interfaces.</li>
          <li>Contributions to open-source projects or a personal portfolio.</li>
        </ul>
        <p><strong>Location: Nablus, Palestine (Hybrid or Remote)</strong></p>
      </>
    ),
  },
 
};

function CareerFormSection() {
  const [selectedJob, setSelectedJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef(null);

  const handleJobSelect = (jobKey) => {
    setSelectedJob(jobKey);
    setShowForm(false);
  };

  const handleBack = () => {
    setSelectedJob(null);
    setShowForm(false);
  };

  const handleApplyClick = () => {
    setShowForm(true);
  };

  if (!selectedJob) {
    return (
      <div className="career">
        <h1 className="career-title">Available Positions</h1>
        <div className="job-list">
          {Object.keys(jobs).map((jobKey) => (
            <div key={jobKey} className="job-card" onClick={() => handleJobSelect(jobKey)}>
              <h2>{jobs[jobKey].title}</h2>
              <p>Click for details and to apply.</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="career">
      <button onClick={handleBack} className="back-btn">
        &larr; Back to Job List
      </button>
      <div className="job-details">
        <h1>{jobs[selectedJob].title}</h1>
        <div className="job-description">{jobs[selectedJob].description}</div>

        {!showForm && (
          <button onClick={handleApplyClick} className="apply-now-btn">
            Apply Now
          </button>
        )}
      </div>

      {showForm && (
        <div className="career-form">
          <form
            ref={formRef}
            action="https://formsubmit.co/info@peony-technologies.com"
            method="POST"
            encType="multipart/form-data"
          >
            <input type="hidden" name="_subject" value={`New Application: ${jobs[selectedJob].title}`} />
            <input type="hidden" name="Job Title" value={jobs[selectedJob].title} />
            <input type="hidden" name="_captcha" value="false" />
            <input type="hidden" name="_template" value="table" />

            <input type="text" name="name" placeholder="Full Name" className="career-form-txt" required />

            <input type="tel" name="phone" placeholder="Phone Number" className="career-form-phone" required />

            <input type="email" name="email" placeholder="Email Address" className="career-form-email" required />

            <input type="text" name="experience" defaultValue="1-3 years" placeholder="Years of Experience" className="career-form-experience" required />

            <textarea name="cover_letter" placeholder="Why do you think you're a good fit for this position?" className="career-form-txtarea" required></textarea>

            <div className="file">
              <h2 className="name">Upload your CV (PDF, DOC, DOCX)</h2>
              <input type="file" name="attachment" accept=".doc,.docx,.pdf" required />
            </div>

            <p className="note">Please ensure your CV includes a link to your portfolio.</p>

            <input type="submit" value="Submit Application" className="career-form-btn" />
          </form>
        </div>
      )}
    </div>
  );
}

export default CareerFormSection;
